import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { CreditCard, Loader2, CheckCircle } from 'lucide-react';

/**
 * RazorpayPayButton
 * Props:
 *   entityType: "tax_case" | "ad_booking"
 *   entityId:   string
 *   amountInr:  number (display only — backend re-validates)
 *   label:      optional button label
 *   onPaid:     optional callback (paymentId) after successful payment
 *   prefill:    optional { name, email, contact }
 *   variant:    "primary" | "compact"
 */
const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(false);
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const s = document.createElement('script');
    s.src = RAZORPAY_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const RazorpayPayButton = ({
  entityType,
  entityId,
  amountInr,
  label,
  onPaid,
  prefill,
  variant = 'primary',
}) => {
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);

  const handlePay = async () => {
    if (!entityId || !entityType) {
      toast.error('Missing payment details');
      return;
    }
    setLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error('Could not load Razorpay. Please check your internet.');
        setLoading(false);
        return;
      }

      // Create order on backend
      const orderRes = await axios.post(`${API}/payments/create-order`, {
        entity_type: entityType,
        entity_id: entityId,
        customer_name: prefill?.name,
        customer_email: prefill?.email,
        customer_phone: prefill?.contact,
      });
      const { order_id, amount, currency, key_id, prefill: backendPrefill } = orderRes.data;

      const options = {
        key: key_id,
        amount,
        currency: currency || 'INR',
        name: 'Arthik',
        description:
          entityType === 'tax_case'
            ? 'Tax Service Payment'
            : 'Advertisement Booking',
        order_id,
        prefill: {
          name: prefill?.name || backendPrefill?.name || '',
          email: prefill?.email || backendPrefill?.email || '',
          contact: prefill?.contact || backendPrefill?.contact || '',
        },
        theme: { color: '#FF6B35' },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast('Payment cancelled', { description: 'You can pay anytime from your dashboard.' });
          },
        },
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPaid(true);
            toast.success('Payment successful! 🎉', {
              description: `Receipt: ${response.razorpay_payment_id.slice(0, 16)}…`,
            });
            if (onPaid) onPaid(response.razorpay_payment_id);
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Payment verification failed');
          } finally {
            setLoading(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        const reason = resp?.error?.description || 'Payment failed';
        toast.error(reason);
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not start payment');
      setLoading(false);
    }
  };

  if (paid) {
    return (
      <span
        data-testid={`pay-paid-${entityId}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '0.5rem 0.9rem',
          background: '#ECFDF5',
          color: '#065F46',
          fontWeight: 700,
          fontSize: '0.85rem',
          borderRadius: 999,
          border: '1px solid #A7F3D0',
        }}
      >
        <CheckCircle size={14} /> Paid
      </span>
    );
  }

  const baseStyle =
    variant === 'compact'
      ? {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '0.5rem 0.9rem',
          background: 'linear-gradient(135deg, #FF6B35 0%, #F97316 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '0.85rem',
          border: 'none',
          borderRadius: 8,
          cursor: loading ? 'wait' : 'pointer',
          boxShadow: '0 2px 6px rgba(255,107,53,0.25)',
          opacity: loading ? 0.7 : 1,
        }
      : {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '0.85rem 1.5rem',
          background: 'linear-gradient(135deg, #FF6B35 0%, #F97316 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '0.95rem',
          border: 'none',
          borderRadius: 10,
          cursor: loading ? 'wait' : 'pointer',
          boxShadow: '0 4px 12px rgba(255,107,53,0.35)',
          opacity: loading ? 0.7 : 1,
        };

  return (
    <button
      onClick={handlePay}
      disabled={loading}
      style={baseStyle}
      data-testid={`pay-now-${entityId}`}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
      {loading ? 'Opening…' : label || `Pay ₹${Number(amountInr || 0).toLocaleString('en-IN')}`}
    </button>
  );
};

export default RazorpayPayButton;
